/**
 * Datas de telemetria: do banco (UTC) para o relógio da fábrica.
 *
 * O backend guarda todo instante de máquina em UTC. Quem lê a tela quer a hora
 * LOCAL da máquina (`Device.time_zone`, herdado da IoT Company) — não a do
 * browser, que pode estar em outro país.
 *
 * Duas famílias de string chegam aqui, e confundi-las desloca tudo:
 *
 *  - `/api/resource/*` (REST cru do Frappe) devolve UTC sem sufixo:
 *    `"2026-08-24 12:00:00"` → use `parseUtc`.
 *  - endpoints de produto (painel público, tracker, responder) já convertem e
 *    devolvem a hora de parede da máquina + o campo `time_zone` → use
 *    `parseWallTime`, que precisa do fuso para achar o instante.
 *
 * `new Date("2026-08-24 12:00:00")` nunca é resposta: o V8 interpreta como hora
 * local do browser (o bug que deslocava tudo em 3h) e o Safari devolve
 * Invalid Date. Todo parse passa por aqui.
 *
 * Sem dependência nova: `Intl.DateTimeFormat` já faz conversão de fuso.
 */

/** Fuso usado quando a máquina não declara o dela (device antigo sem cadastro). */
export const FUSO_PADRAO = "America/Sao_Paulo";

/** Escolhe o fuso a aplicar: o da máquina, o da empresa, ou o padrão. */
export function resolveTimeZone(
  ...candidatos: (string | null | undefined)[]
): string {
  for (const tz of candidatos) {
    if (tz && tz.trim()) return tz.trim();
  }
  return FUSO_PADRAO;
}

const COM_FUSO = /([zZ]|[+-]\d{2}:?\d{2})$/;

/**
 * String UTC do banco → Date. Aceita `"YYYY-MM-DD HH:mm:ss"`, ISO com `T` e
 * valores que já trazem sufixo de fuso (aí respeita o que veio).
 */
export function parseUtc(valor?: string | number | Date | null): Date | null {
  if (valor === null || valor === undefined || valor === "") return null;
  if (valor instanceof Date) return isNaN(valor.getTime()) ? null : valor;
  if (typeof valor === "number") return new Date(valor);

  const texto = String(valor).trim().replace(" ", "T");
  if (!texto) return null;
  const data = new Date(COM_FUSO.test(texto) ? texto : `${texto}Z`);
  return isNaN(data.getTime()) ? null : data;
}

/** Offset do fuso (ms) no instante dado — positivo a leste de Greenwich. */
function offsetDoFuso(instante: Date, timeZone: string): number {
  const partes = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instante);

  const campo: Record<string, number> = {};
  for (const parte of partes) {
    if (parte.type !== "literal") campo[parte.type] = Number(parte.value);
  }
  const comoUtc = Date.UTC(
    campo.year,
    campo.month - 1,
    campo.day,
    campo.hour % 24, // alguns runtimes devolvem 24 para a meia-noite
    campo.minute,
    campo.second,
  );
  return comoUtc - instante.getTime();
}

/**
 * Hora de parede da máquina → Date (instante real).
 *
 * Use para o que o usuário digita (input datetime-local), para limites de
 * janela e para datas que o backend já mandou convertidas.
 */
export function parseWallTime(
  valor: string | Date | null | undefined,
  timeZone: string,
): Date | null {
  if (valor === null || valor === undefined || valor === "") return null;

  let ano: number,
    mes: number,
    dia: number,
    hora = 0,
    minuto = 0,
    segundo = 0;

  if (valor instanceof Date) {
    if (isNaN(valor.getTime())) return null;
    [ano, mes, dia, hora, minuto, segundo] = [
      valor.getFullYear(),
      valor.getMonth() + 1,
      valor.getDate(),
      valor.getHours(),
      valor.getMinutes(),
      valor.getSeconds(),
    ];
  } else {
    const m = String(valor)
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (!m) return null;
    [ano, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
    [hora, minuto, segundo] = [Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0)];
  }

  const base = Date.UTC(ano, mes - 1, dia, hora, minuto, segundo);
  // duas passadas: a primeira estima o offset, a segunda corrige quando a
  // estimativa caiu do outro lado de uma mudança de horário
  let instante = base - offsetDoFuso(new Date(base), timeZone);
  instante = base - offsetDoFuso(new Date(instante), timeZone);
  return new Date(instante);
}

/**
 * Início do dia (00:00) na parede da máquina.
 *
 * Janelas como "hoje" e "últimos 7 dias" viram um dia inteiro do turno da
 * fábrica; cortar pela meia-noite do browser deslocaria o primeiro e o último
 * balde de todo gráfico.
 */
export function startOfDayInTz(instante: Date, timeZone: string): Date {
  const parede = toDeviceLocalSqlString(instante, timeZone);
  return parseWallTime(`${parede.slice(0, 10)} 00:00:00`, timeZone) ?? instante;
}

/**
 * Início da hora (HH:00) na parede da máquina.
 *
 * Não é o mesmo que truncar o epoch: fusos de meia hora (Índia, Newfoundland)
 * não caem na virada de hora UTC.
 */
export function startOfHourInTz(instante: Date, timeZone: string): Date {
  const parede = toDeviceLocalSqlString(instante, timeZone);
  return parseWallTime(`${parede.slice(0, 13)}:00:00`, timeZone) ?? instante;
}

/** Formata um instante no fuso da máquina. */
export function formatInTz(
  data: Date | null,
  timeZone: string,
  opcoes: Intl.DateTimeFormatOptions = {},
  locale?: string,
): string {
  if (!data || isNaN(data.getTime())) return "";
  return new Intl.DateTimeFormat(locale || "pt-BR", { timeZone, ...opcoes }).format(data);
}

/** Atalho: string UTC do banco → texto no fuso da máquina. */
export function formatUtcInTz(
  valor: string | number | Date | null | undefined,
  timeZone: string,
  opcoes: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  },
  locale?: string,
): string {
  return formatInTz(parseUtc(valor), timeZone, opcoes, locale);
}

/**
 * Rótulo de eixo "HH:mm" no fuso da máquina.
 *
 * `hourCycle: "h23"` é obrigatório: sem ele a meia-noite sai como "24:00" em
 * parte dos runtimes, e os eixos usam justamente "00:00" para saber que
 * viraram o dia.
 */
export function formatHourMinuteInTz(data: Date | null, timeZone: string): string {
  return formatInTz(data, timeZone, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}

/** Rótulo de eixo "dd/MM" no fuso da máquina. */
export function formatDayMonthInTz(data: Date | null, timeZone: string): string {
  return formatInTz(data, timeZone, { day: "2-digit", month: "2-digit" });
}

/** Instante → `"YYYY-MM-DD HH:mm:ss"` em UTC, para filtrar `/api/resource/*`. */
export function toUtcSqlString(data: Date): string {
  return data.toISOString().slice(0, 19).replace("T", " ");
}

/**
 * Instante → `"YYYY-MM-DD HH:mm:ss"` na hora da máquina, para os endpoints de
 * produto (que interpretam a data no fuso do device).
 */
export function toDeviceLocalSqlString(data: Date, timeZone: string): string {
  const partes = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(data);

  const campo: Record<string, string> = {};
  for (const parte of partes) {
    if (parte.type !== "literal") campo[parte.type] = parte.value;
  }
  const hora = campo.hour === "24" ? "00" : campo.hour;
  return `${campo.year}-${campo.month}-${campo.day} ${hora}:${campo.minute}:${campo.second}`;
}
